<template>
  <section class="page">
    <div class="eyebrow">❌ 错题本 · 错误归因 · 同类训练</div>
    <div class="heading">
      <div>
        <h1>错题本 · <em>错题 → 病因 → 镜像题</em></h1>
        <p>输入错题，AI 自动归因错误类型，生成正误对比卡片与同类练习题</p>
      </div>
    </div>

    <!-- 录入区 -->
    <div class="mistake-form">
      <textarea v-model="stem" class="ai-input" placeholder="原题内容 / 题干（必填）" :disabled="loading" />
      <div class="answer-row">
        <input v-model="wrongAnswer" class="ai-input wrong" placeholder="我的错答" :disabled="loading">
        <input v-model="rightAnswer" class="ai-input right" placeholder="正确答案" :disabled="loading">
        <button class="btn" :disabled="loading || !stem.trim()" @click="submit">{{ loading ? '分析中…' : '📝 AI 归因分析' }}</button>
      </div>
    </div>

    <!-- 分析结果 -->
    <ai-output-card v-if="current || loading" :title="`📊 错题画像 · ${profileType}`" :loading="loading" :error="error" :model="record && record.model" :mock="demo" :warning="demoWarning">
      <template v-if="current">
        <div class="profile-chips">
          <span class="chip">{{ current.profile.type }}</span>
          <span class="chip">{{ current.profile.system }}</span>
          <span class="chip">{{ current.profile.difficulty }}</span>
        </div>
        <p class="reason">{{ current.profile.reason }}</p>
        <div class="compare-grid">
          <article class="compare wrong-card">
            <h3>❌ 为什么错</h3>
            <p>{{ current.compare.wrongWhy }}</p>
            <div class="ans-row">错答：<b class="wrong-ans">{{ record.wrongAnswer || '—' }}</b></div>
          </article>
          <article class="compare right-card">
            <h3>✅ 为什么对</h3>
            <p>{{ current.compare.rightWhy }}</p>
            <div class="ans-row">正答：<b class="right-ans">{{ record.rightAnswer || '—' }}</b></div>
          </article>
        </div>
        <div class="section-title">🔑 关键鉴别点</div>
        <article class="summary">{{ current.compare.key }}</article>
        <div class="section-title" style="margin-top:18px">同类练习题</div>
        <article v-for="(ex, i) in current.exercises || []" :key="i" class="exercise">
          <p class="ex-stem">{{ i + 1 }}. {{ ex.stem }}</p>
          <div class="ex-options">
            <span v-for="(opt, j) in ex.options" :key="j" class="ex-opt" :class="{ correct: ex.answer === String.fromCharCode(65 + j) }">{{ String.fromCharCode(65 + j) }}. {{ opt }}</span>
          </div>
          <p class="ex-explain">💡 {{ ex.explain }}</p>
        </article>
      </template>
    </ai-output-card>

    <!-- 错题列表 -->
    <div class="section-title" style="margin-top:26px">📚 我的错题（{{ list.length }}）</div>
    <div v-if="listDemo" class="ai-output-warning" style="margin-top:0">数据库未连接，以下列表为内存数据，重启后丢失</div>
    <div class="list" v-if="list.length">
      <article v-for="m in list" :key="m.id">
        <span>{{ m.errorType || '未分类' }} · {{ m.system || '' }}</span>
        <b style="display:block;margin-top:4px">{{ shortStem(m.stem) }}</b>
        <p class="hint">{{ m.createdAt && String(m.createdAt).slice(0, 10) }}</p>
        <div class="row-actions">
          <button class="btn btn-ghost btn-sm" @click="view(m)">查看归因</button>
          <button class="btn btn-ghost btn-sm danger" @click="remove(m)">删除</button>
        </div>
      </article>
    </div>
    <gate-banner :error="gateError" />
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import AIOutputCard from '../components/AIOutputCard.vue';
import GateBanner from '../components/GateBanner.vue';
import EmptyState from '../components/EmptyState.vue';

export default {
  name: 'MistakesView',
  components: { AIOutputCard, GateBanner, EmptyState },
  data: () => ({
    stem: '', wrongAnswer: '', rightAnswer: '',
    loading: false, error: '',
    gateError: null,
    current: null, record: null, demo: false,
  }),
  computed: {
    ...mapState('mistakes', { listState: 'list', listDemo: 'demo' }),
    list() { return this.listState || []; },
    profileType() { return (this.current && this.current.profile && this.current.profile.type) || '分析中'; },
    demoWarning() { return this.demo ? '数据库未连接，本次归因仅保存在内存中' : ''; },
  },
  created() { this.load(); },
  methods: {
    ...mapActions('mistakes', ['load', 'create', 'remove']),
    async submit() {
      if (!this.stem.trim() || this.loading) return;
      this.loading = true;
      this.error = '';
      try {
        const res = await this.create({ stem: this.stem.trim(), wrongAnswer: this.wrongAnswer.trim(), rightAnswer: this.rightAnswer.trim() });
        this.record = res.record || res;
        this.current = this.record.attribution;
        this.demo = !!res.demo;
        this.stem = ''; this.wrongAnswer = ''; this.rightAnswer = '';
      } catch (e) {
        this.gateError = { code: e.code, msg: e.msg || e.message };
        this.error = this.gateError.msg;
      } finally {
        this.loading = false;
      }
    },
    view(m) { this.record = m; this.current = m.attribution; this.demo = false; window.scrollTo({ top: 0, behavior: 'smooth' }); },
    shortStem(s) { return s.length > 60 ? s.slice(0, 60) + '…' : s; },
  },
};
</script>

<style scoped>
.mistake-form { margin-top: 18px; }
.answer-row { display: flex; gap: 10px; margin-top: 10px; flex-wrap: wrap; }
.answer-row .ai-input { flex: 1; min-width: 160px; }
.ai-input.wrong:focus { border-color: var(--danger); }
.ai-input.right:focus { border-color: var(--success); }
.profile-chips { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
.reason { color: var(--text-2); line-height: 1.8; }
.compare-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 14px; }
.compare { border-radius: var(--radius); padding: 14px; }
.compare h3 { margin: 0 0 8px; font-size: 13px; }
.compare p { margin: 0; color: var(--text-2); font-size: 12px; line-height: 1.7; }
.wrong-card { background: var(--danger-a07); border: 1px solid var(--danger-a15); }
.right-card { background: var(--success-a07); border: 1px solid var(--success-a40); }
.ans-row { margin-top: 10px; font-size: 12px; color: var(--text-3); }
.wrong-ans { color: var(--danger); }
.right-ans { color: var(--success); }
.exercise { background: var(--bg-1); border: 1px solid var(--border-1); border-radius: var(--radius); padding: 14px; margin-bottom: 10px; }
.ex-stem { font-size: 13px; margin: 0 0 8px; }
.ex-options { display: grid; gap: 6px; }
.ex-opt { font-size: 12px; color: var(--text-2); padding: 6px 10px; border-radius: 6px; background: var(--bg-2); }
.ex-opt.correct { color: var(--success); border: 1px solid var(--success-a40); }
.ex-explain { font-size: 12px; color: var(--text-3); margin: 8px 0 0; }
.row-actions { display: flex; gap: 8px; margin-top: 10px; }
.btn-sm { padding: 4px 12px; font-size: 12px; }
.btn-sm.danger:hover { color: var(--danger); border-color: var(--danger); }
@media (max-width: 700px) { .compare-grid { grid-template-columns: 1fr; } }
</style>
